<template>
	<Modal
		:allow-close="false"
		:hide-actions="true"
		:max-width="'max-w-[485px]'"
		rounded-size="rounded-[10px]"
		:prevent-scrolling="false"
		:remove-padding="true"
	>
		<template #body>
			<!-- The shared checklist renders its own page-sized heading; the modal uses the Figma h3. -->
			<div class="p-6 font-[Inter] [&_h1]:text-xl [&_h1]:text-gray-900">
				<AnalyzeChecklist
					:stage="stage"
					:slow="slow"
					:can-skip="canSkip"
					:steps="STEPS"
					@complete="finish"
					@skip="finish"
				/>
			</div>
		</template>
	</Modal>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import Modal from '../../components/modal.vue';
import AnalyzeChecklist from '../../components/onboarding/analyze-checklist.vue';
import { getResumeAnalysis, startResumeAnalysis } from '../../api/onboarding';
import { useExistingUserAutoApplyStore } from '../../store/existing-user-auto-apply.store';

const STEPS = [
	'Reading your resume',
	'Connecting to your profile history',
	'Finding your best matches',
];

/** Matches ResolveResumeParseStage; index order is the pipeline order. */
const STAGES = ['pending', 'extracting', 'parsed', 'ready'];

// Same pacing as the onboarding Analyze page.
const POLL_INTERVAL_MS = 1500;
const TICK_MS = 1000;
const SLOW_NOTICE_MS = 20_000;
const SKIP_OFFER_MS = 40_000;
const ANALYSIS_TIMEOUT_MS = 90_000;

const store = useExistingUserAutoApplyStore();

// Monotonic, so a stale or out-of-order poll response can never rewind the checklist.
const maxRank = ref(0);
const elapsedMs = ref(0);

const stage = computed(() => STAGES[maxRank.value]);
const slow = computed(() => elapsedMs.value >= SLOW_NOTICE_MS);
const canSkip = computed(() => elapsedMs.value >= SKIP_OFFER_MS);

let lastStatus = null;
let active = true;
let finished = false;
let pollId;
let tickId;
let timeoutId;

const stopWaiting = () => {
	clearInterval(pollId);
	clearInterval(tickId);
	clearTimeout(timeoutId);
};

const record = (status) => {
	lastStatus = status;

	const rank = STAGES.indexOf(status?.stage);
	if (rank > maxRank.value) {
		maxRank.value = rank;
	}

	if (stage.value === 'ready') {
		stopWaiting();
	}
};

const poll = async (request) => {
	try {
		record(await request());
	} catch {
		// The next poll, or the timeout, moves the user on.
	}
};

/** Hands off with the last status seen; a timed-out analysis still reaches the questions. */
const finish = () => {
	if (finished) {
		return;
	}
	finished = true;

	stopWaiting();
	store.finishAnalysis(lastStatus);
};

onMounted(async () => {
	tickId = setInterval(() => {
		elapsedMs.value += TICK_MS;
	}, TICK_MS);
	timeoutId = setTimeout(finish, ANALYSIS_TIMEOUT_MS);

	// A fresh upload is already in the parse pipeline; only a resume on file needs a kickoff.
	await poll(store.uploadedNewResume ? getResumeAnalysis : startResumeAnalysis);

	if (active && stage.value !== 'ready') {
		pollId = setInterval(() => poll(getResumeAnalysis), POLL_INTERVAL_MS);
	}
});

onBeforeUnmount(() => {
	active = false;
	stopWaiting();
});
</script>
